// src/lib/auth.js
import { auth } from "./firebase";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  GoogleAuthProvider,
  signInWithPopup,
  onAuthStateChanged
} from "firebase/auth";

/**
 * Register a new user using email and password.
 * @param {string} email
 * @param {string} password
 * @returns {Promise<import('firebase/auth').UserCredential>}
 */
export const signUpWithEmail = async (email, password) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    );
    return userCredential.user;
  } catch (error) {
    throw error;
  }
};

/**
 * Sign in an existing user using email and password.
 * @param {string} email
 * @param {string} password
 * @returns {Promise<import('firebase/auth').UserCredential>}
 */
export const signInWithEmail = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password
    );
    return userCredential.user;
  } catch (error) {
    throw error;
  }
};

/**
 * Send a password reset email to the specified address.
 * @param {string} email
 */
export const resetPassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error) {
    throw error;
  }
};

/**
 * Sign in with Google Popup.
 * @returns {Promise<import('firebase/auth').UserCredential>}
 */
const googleProvider = new GoogleAuthProvider();
export const signInWithGoogle = async () => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    return result.user;
  } catch (error) {
    throw error;
  }
};

/**
 * Sign out the currently logged-in user.
 */
export const logout = async () => {
  try {
    await signOut(auth);
    // Clear any local authentication state
    localStorage.removeItem('isAuthenticated');
  } catch (error) {
    throw error;
  }
};

/**
 * Subscribe to auth state changes
 * @param {Function} callback - Function to call when auth state changes
 * @returns {Function} - Unsubscribe function
 */
export const subscribeToAuthChanges = (callback) => {
  return onAuthStateChanged(auth, (user) => {
    if (user) {
      // User is signed in
      localStorage.setItem('isAuthenticated', 'true');
      callback(user);
    } else {
      // User is signed out
      localStorage.removeItem('isAuthenticated');
      callback(null);
    }
  });
};

/**
 * Get the current authenticated user
 * @returns {import('firebase/auth').User|null}
 */
export const getCurrentUser = () => {
  return auth.currentUser;
};
